import {useEffect, useState} from 'react';
import type {DoppeViewerPageInfo} from '../../client-server-common/viewer-pages/doppe-viewer-pages';
import {doppeViewerPageInfoResolvePageId} from '../../client-server-common/viewer-pages/doppe-viewer-pages';
import type {DoppeViewerRouterClientService} from './doppe-viewer-router-client-service';
import {useBymoPageProps} from '../../client-server-common/bymo-pages/bymo-page-props';
import {useDoppeViewerClientServices} from "../doppe-user-app/doppe-viewer-client-services-react";

/**
 * React hook that subscribes to router page changes and triggers re-renders
 * 
 * This hook returns the current page info from the router and automatically
 * updates when navigation occurs, causing the component to re-render.
 * 
 * @param router - The viewer router service
 * @returns The current page info, or undefined if not initialized
 * 
 * @example
 * ```typescript
 * const pageInfo = useDoppeViewerRouterPageInfo(clientServices.viewerRouterClientService);
 * 
 * if (doppeViewerPageInfoIsItemPage(pageInfo)) {
 *   // Render item page
 * }
 * ```
 */
export function useDoppeViewerRouterPageInfo(
    router: DoppeViewerRouterClientService
): DoppeViewerPageInfo {
    // Store current page info in state to trigger re-renders
    const [pageInfo, setPageInfo] = useState<DoppeViewerPageInfo>(() => router.getCurrentPageInfo());
    
    // Subscribe to router changes
    useEffect(() => {
        // Update state when router navigates
        const unsubscribe = router.subscribe((newPageInfo) => {
            setPageInfo(newPageInfo);
        });
        
        // Cleanup subscription on unmount
        return unsubscribe;
    }, [router]); // Only re-subscribe if router instance changes
    
    return pageInfo;
}

/**
 * React hook that returns the current page ID for analytics tracking.
 * Returns DoppeDtoInnerPageKnownIds.Home when on the home page.
 * 
 * This hook combines the router page info with bymo page props to resolve
 * the actual page ID. It uses the existing hooks internally.
 * 
 * @returns The page ID string, or undefined if page info is not available
 * 
 * @example
 * ```typescript
 * const pageId = useCurrentPageId();
 * 
 * clientServices.viewerAnalyticsClient.logEvent('uouSomeEvent', {
 *   ...otherFields,
 *   innerPageId: pageId,
 * });
 * ```
 */
export function useCurrentPageId(): string | undefined {
    const clientServices = useDoppeViewerClientServices();
    const bymoPageProps = useBymoPageProps();
    const currentPageInfo = clientServices.viewerRouterClientService.getCurrentPageInfo();
    
    return doppeViewerPageInfoResolvePageId(currentPageInfo, bymoPageProps) ?? undefined;
}
